import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import { pictureUrl } from '../lib/api';
import { EditButton } from './admin/EditorShell';
import { SchemaEditor } from './admin/SchemaEditor';

export const SECTION_SCHEMA = [
  { key: 'title', label: 'Seksjonstittel' },
  { key: 'subtitle', label: 'Undertittel' },
  { key: 'cards', label: 'Boksar', type: 'list', itemLabel: 'boks', schema: [
    { key: 'image', label: 'Bilete', type: 'image' },
    { key: 'title', label: 'Tittel' },
    { key: 'subtitle', label: 'Undertittel' },
    { key: 'text', label: 'Kort tekst på boksen', type: 'textarea', rows: 2 },
    { key: 'popup', label: 'Tekst i popup (tom linje = nytt avsnitt; tom = ingen popup)', type: 'textarea', rows: 4 },
    { key: 'link', label: 'Lenke (valfritt)', type: 'url' },
  ] },
];

const Card = ({ c, onOpen }) => {
  const { t } = useT();
  const inner = (
    <>
      {c.image && <img src={pictureUrl(c.image)} alt={c.title} loading="lazy" className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500" />}
      <div className="p-5">
        {c.subtitle && <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-[10px] mb-1">{t(c.subtitle)}</p>}
        <h3 className="text-white font-bold text-xl tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(c.title)}</h3>
        {c.text && <p className="text-white/60 text-sm mt-2 leading-relaxed">{t(c.text)}</p>}
        {(c.popup || c.link) && <p className="text-[#ff2e2e] text-xs font-bold mt-3">{t('Les meir')} →</p>}
      </div>
    </>
  );
  const cls = 'group text-left rounded-3xl overflow-hidden bg-[#1a1424] border border-white/5 hover:border-[#ff2e2e]/50 transition-colors block';
  if (c.popup) return <button type="button" onClick={() => onOpen(c)} className={cls}>{inner}</button>;
  if (c.link) return <a href={c.link} target="_blank" rel="noreferrer noopener" className={cls}>{inner}</a>;
  return <div className={cls}>{inner}</div>;
};

const CustomSections = () => {
  const { content } = useContent();
  const { user } = useAuth();
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(null);
  const sections = content.sections || [];
  if (sections.length === 0 && !user?.is_admin) return null;

  return (
    <>
      {sections.map((s) => (
        <section key={s.id} id={`seksjon-${s.id}`} className="bg-black py-16 md:py-24 border-t border-white/5" data-testid={`custom-section-${s.id}`}>
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="flex items-start justify-between gap-4 mb-8">
              <div>
                {s.subtitle && <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm mb-2">{t(s.subtitle)}</p>}
                <h2 className="text-4xl md:text-7xl font-bold text-white tracking-tighter" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(s.title)}</h2>
              </div>
              <EditButton onClick={() => setEditing(true)} testId={`edit-section-${s.id}`} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(s.cards || []).map((c) => <Card key={c.id} c={c} onOpen={setOpen} />)}
            </div>
          </div>
        </section>
      ))}
      {user?.is_admin && sections.length === 0 && (
        <div className="bg-black py-8 border-t border-white/5"><div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <button type="button" onClick={() => setEditing(true)} data-testid="add-section-button" className="w-full py-5 rounded-3xl border border-dashed border-[#ffb400]/50 text-[#ffb400] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#ffb400]/5"><Plus size={18} /> Legg til eigen seksjon (berre synleg for admin)</button>
        </div></div>
      )}
      {editing && <SchemaEditor contentKey="sections" title="Rediger eigne seksjonar" initial={sections} schema={SECTION_SCHEMA} itemLabel="seksjon" onClose={() => setEditing(false)} />}
      {open && createPortal(
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="section-card-dialog">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(null)} />
          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-[#0f0a17] border border-white/10 shadow-2xl animate-[fadeUp_0.25s_ease-out]">
            <button type="button" onClick={() => setOpen(null)} aria-label="Lukk" className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 border border-white/15 text-white flex items-center justify-center hover:border-[#ff2e2e] hover:text-[#ff2e2e]"><X size={18} /></button>
            {open.image && <img src={pictureUrl(open.image)} alt={open.title} className="w-full aspect-[16/9] object-cover" />}
            <div className="p-7">
              {open.subtitle && <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-xs mb-1">{t(open.subtitle)}</p>}
              <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(open.title)}</h3>
              {open.popup.split(/\n\s*\n/).map((p, i) => <p key={i} className="text-white/70 leading-relaxed mb-3">{p}</p>)}
              {open.link && <a href={open.link} target="_blank" rel="noreferrer noopener" className="inline-flex mt-2 px-6 py-3 rounded-full bg-[#ff2e2e] text-black font-bold">{t('Les meir')}</a>}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CustomSections;
